/**
 * Wording for the duplicate-PO banner (DuplicatePoWarning.tsx), kept out
 * of the component file so it can be unit-tested and so the component
 * file exports only components (react-refresh rule).
 *
 * Kanban f1f3600c: an `invoiced` match means the PO already shipped and
 * was billed (a completed portal order or Sage invoice history), so the
 * banner names the invoice date and the earlier order and asks whether
 * this is a re-order. `open` matches keep the original "adjust the
 * existing order" wording.
 */
import type { DuplicatePoOrderSummary } from '../../services/portalOrders';
import { formatDate } from '../../utils/dateFormatters';

export const DUPLICATE_PO_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submission_failed: 'Submission Failed',
  submitted_to_sage: 'Submitted to Sage',
  confirmed_in_sage: 'Confirmed in Sage',
  accepted: 'Accepted',
  shipped: 'Shipped',
  order_completed: 'Completed',
  cancelled: 'Cancelled',
  invoiced: 'Invoiced in Sage',
};

export function duplicateOrderLabel(d: DuplicatePoOrderSummary): string {
  if (d.sage_order_no) return `#${d.sage_order_no}`;
  if (d.id) return `#${d.id.slice(0, 8)}`;
  return d.invoice_no ? `invoice ${d.invoice_no}` : 'an earlier order';
}

export function duplicateInvoicedWhen(d: DuplicatePoOrderSummary): string | null {
  return d.invoice_date ? formatDate(d.invoice_date) : null;
}

/** Headline + subtitle for the banner, driven by what kinds of matches exist. */
export function duplicatePoCopy(duplicates: DuplicatePoOrderSummary[]): {
  headline: string;
  subtitle: string;
} {
  const open = duplicates.filter((d) => d.match_kind !== 'invoiced');
  const invoiced = duplicates.filter((d) => d.match_kind === 'invoiced');
  const po = duplicates.find((d) => d.po_number)?.po_number;
  const poText = po ? `PO ${po}` : 'This PO number';

  if (open.length === 0 && invoiced.length > 0) {
    const first = invoiced[0];
    const when = duplicateInvoicedWhen(first);
    const headline =
      invoiced.length === 1
        ? `${poText} was already invoiced for this customer${when ? ` on ${when}` : ''} as ${duplicateOrderLabel(first)}.`
        : `${poText} was already invoiced for this customer ${invoiced.length} times.`;
    return {
      headline,
      subtitle:
        'Is this a re-order? Confirm with the customer before creating a new order: the earlier one has already shipped and been billed.',
    };
  }

  const headline =
    open.length === 1 && invoiced.length === 0
      ? 'This PO number already exists for this customer.'
      : `This PO number matches ${duplicates.length} existing orders for this customer.`;
  const subtitle =
    invoiced.length > 0
      ? `You may want to adjust the open ${open.length === 1 ? 'order' : 'orders'} instead of creating a new one. ${invoiced.length === 1 ? 'One match' : `${invoiced.length} matches`} already shipped and ${invoiced.length === 1 ? 'was' : 'were'} invoiced. Is this a re-order?`
      : `You may want to adjust the existing ${open.length === 1 ? 'order' : 'orders'} instead of creating a new one.`;
  return { headline, subtitle };
}
